import { clsx, generateUniqueId, getLocalStorageMemoizedValue } from '@digital-spiders/misc-utils';
import { getHookPointStoredTrackingData } from '@hook-point/tracking-data';
import * as Sentry from '@sentry/react';
import React, { useEffect, useRef, useState } from 'react';
import { ROOT_DOMAIN } from '../../constants';
import { useGlobalState } from '../../state/globalStateContext';
import { PageDocument } from '../../types/types';
import { getPageDocumentUrl } from '../../utils/sanity';
import { withDataLayer } from '../../utils/utils';
import * as styles from './Calendar.module.scss';

// Define window.Calendly for TypeScript
declare global {
  interface Window {
    Calendly: any;
  }
}

export interface CalendlyCalendarProps {
  pageToGoAfterSubmit?: PageDocument;
  calendarLink: string;
  className?: string;
  onLoad?: () => void;
}

function CalendlyCalendar(props: CalendlyCalendarProps): React.ReactElement {
  const { pageToGoAfterSubmit, calendarLink, className, onLoad } = props;
  const { isCalendlyScriptLoaded, setIsCalendlyScriptLoaded } = useGlobalState();
  const calendlyEventTriggered = useRef(false);
  const calendlyDivId = useRef(`calendly-embed-${Math.random().toString(36).substring(2, 11)}`);
  const isWidgetInitialized = useRef(false);

  // Load the Calendly script first
  useEffect(() => {
    // Only load the script once
    if (document.getElementById('calendly-script')) {
      return;
    }

    const script = document.createElement('script');
    script.id = 'calendly-script';
    script.src = 'https://assets.calendly.com/assets/external/widget.js';
    script.async = true;
    script.onload = () => setIsCalendlyScriptLoaded(true);
    document.body.appendChild(script);

    return () => {
      // We don't want to remove the script when component unmounts
      // as it might be needed by other Calendly embeds
    };
  }, []);

  // Get tracking data and initialize the calendar
  useEffect(() => {
    if (!isCalendlyScriptLoaded || isWidgetInitialized.current) return;

    let isMounted = true;

    // Load tracking data and initialize widget
    const initializeCalendly = async () => {
      try {
        // Get tracking data
        const trackingData = await getHookPointStoredTrackingData({
          rootDomain: ROOT_DOMAIN,
        });

        const filteredCalendlyData = Object.fromEntries(
          Object.entries({
            utm_source: trackingData.utmSource,
            utm_medium: trackingData.utmMedium,
            utm_campaign: trackingData.utmCampaign,
            utm_content: trackingData.utmContent,
            utm_term: trackingData.utmTerm,
          }).filter(([_, value]) => value !== null),
        ) as Record<string, string>;

        // Initialize Calendly after a short delay to ensure the script is fully loaded
        if (isMounted) {
          // Mark as initialized to prevent duplicate initialization,
          // shouldn't happen, but just in case
          isWidgetInitialized.current = true;

          window.Calendly.initInlineWidget({
            url: calendarLink,
            parentElement: document.getElementById(calendlyDivId.current),
            prefill: {},
            utm: {
              ...filteredCalendlyData,
            },
          });

          if (onLoad) {
            onLoad();
          }
        }
      } catch (error) {
        console.error('Error initializing Calendly:', error);
        Sentry.captureException(error);
      }
    };

    initializeCalendly();

    // Set up event listener for Calendly events
    function isCalendlyEvent(e: MessageEvent) {
      return (
        e.origin === 'https://calendly.com' &&
        e.data.event &&
        e.data.event.indexOf('calendly.') === 0
      );
    }

    const messageHandler = function (e: MessageEvent) {
      if (isCalendlyEvent(e)) {
        // Handle event based on type
        if (e.data.event === 'calendly.event_scheduled' && !calendlyEventTriggered.current) {
          // Push to dataLayer for analytics
          withDataLayer(dataLayer => {
            dataLayer.push({
              event: 'calendly-schedule',
              calendar: calendarLink,
              uniqueId: getLocalStorageMemoizedValue(
                'randomly_generated_unique_id',
                generateUniqueId,
              ),
            });
          });

          // This shouldn't happen, but just in case
          calendlyEventTriggered.current = true;

          // Redirect after successful booking if pageToGoAfterSubmit is specified
          if (pageToGoAfterSubmit) {
            window.location.href = getPageDocumentUrl(pageToGoAfterSubmit);
          }
        }
      }
    };

    window.addEventListener('message', messageHandler);

    return () => {
      isMounted = false;
      // Clean up event listener
      window.removeEventListener('message', messageHandler);
    };
  }, [isCalendlyScriptLoaded]);

  return (
    <div
      id={calendlyDivId.current}
      className={clsx(className, styles.calendly)}
      style={{ minWidth: '320px', minHeight: '750px' }}
    ></div>
  );
}

export default CalendlyCalendar;
